// Mail V2 — 邮件列表(320px)。
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  MoreHorizontal,
  Star,
  MailOpen,
  Archive,
  Trash2,
  AlertOctagon,
} from "lucide-react";
import { formatJstDateTime } from "../../../utils/dateTime";
import { type MailMessage } from "../data/demoMailData";

interface MailV2ListProps {
  messages: MailMessage[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  selectedMessageId: string | null;
  onSelectMessage: (id: string) => void;
  onToggleStar: (id: string) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  // Stage Mail-V2: refresh hook 接入 — Real Mode 走 hook refresh,demo 模式不传(用 window.location.reload)。
  onRefresh?: () => void;
  /** Mail V2.5 (2026-08-19): disable the refresh button while an
   *  incremental sync job is in flight (per plan §9 — incremental doesn't
   *  mount the overlay, just disable the button until polling detects
   *  terminal status). */
  refreshDisabled?: boolean;
  // Stage Mail-V2 Step 15: batch selection + batch actions.
  // Phase 1.2 (2026-07-23): selectedIds 现在是 Page 层派生出的 effective set,
  // MailV2List 不再防御不可见 ID。
  selectedIds?: Set<string>;
  /** 选中项中存在未读邮件(用于动态显示「既読/未読」图标按钮)。 */
  hasAnyUnreadSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: () => void;
  onBatchAction?: (
    action:
      | "mark_read"
      | "mark_unread"
      | "star"
      | "unstar"
      | "archive"
      | "trash"
      | "spam"
      | "unspam"
      | "restore_to_inbox"
      | "permanent_delete",
  ) => void;
  // 2026-08-24 mini-body recovery §2.8: mailbox rebuild repair 期间 disable 整个列表。
  disabled?: boolean;
  disabledMessage?: string;
  batchPending?: boolean;
  /** 2026-07-23: Trash folder 时,BulkToolbar 用「完全削除」替代「削除」,
   *  MailV2Detail 的 trash 按钮 disabled。 */
  selectedFolderId?: string;
}

function formatRelativeTime(iso: string): string {
  const value = formatJstDateTime(iso);
  return value === "—" ? "" : value.slice(0, 10).replace(/-/g, "/");
}

export function MailV2List({
  messages,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  selectedMessageId,
  onSelectMessage,
  onToggleStar,
  onPrevPage,
  onNextPage,
  onRefresh,
  refreshDisabled,
  selectedIds,
  hasAnyUnreadSelected,
  onToggleSelect,
  onToggleSelectAll,
  onBatchAction,
  batchPending,
  selectedFolderId,
  disabled,
  disabledMessage,
}: MailV2ListProps) {
  const start = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className={`mv2-list-card ${disabled ? "is-disabled" : ""}`}>
      {disabled && disabledMessage && (
        <div className="mv2-list-disabled-banner">{disabledMessage}</div>
      )}
      <div className="mv2-list-toolbar">
        <input
          type="checkbox"
          aria-label="すべて選択"
          disabled={disabled || messages.length === 0 || !onToggleSelectAll}
          checked={
            !!selectedIds &&
            messages.length > 0 &&
            messages.every((m) => selectedIds.has(m.id))
          }
          onChange={() => onToggleSelectAll?.()}
        />
        <button
          type="button"
          className="mv2-toolbar-btn"
          aria-label="更新"
          title={refreshDisabled ? "同期中です…" : "更新"}
          disabled={refreshDisabled}
          onClick={() => {
            // Stage Mail-V2 真实接入:Real Mode 用父组件传下来的 onRefresh(hook-level),
            // demo mode(没传 onRefresh)退回到整页 reload。
            if (onRefresh) onRefresh();
            else window.location.reload();
          }}
        >
          <RefreshCw
            size={18}
            className={refreshDisabled ? "mv2-toolbar-btn--spinning" : undefined}
          />
        </button>
        <button
          type="button"
          className="mv2-toolbar-btn"
          aria-label="その他"
          title="その他"
          disabled
        >
          <MoreHorizontal size={18} />
        </button>
        <div className="mv2-list-toolbar-spacer" />
        <span className="mv2-list-toolbar-text">
          {start}–{end} / {totalCount}
        </span>
        <button
          type="button"
          className="mv2-toolbar-btn"
          onClick={onPrevPage}
          disabled={currentPage <= 1}
          aria-label="前のページ"
          title="前のページ"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          type="button"
          className="mv2-toolbar-btn"
          onClick={onNextPage}
          disabled={currentPage >= totalPages}
          aria-label="次のページ"
          title="次のページ"
        >
          <ChevronRight size={18} />
        </button>
      </div>
      {/* Stage Mail-V2 Step 15: batch action toolbar
         Phase 1.2 (2026-07-23): 重写为图标按钮 + 动态 read/unread + 「…」popover 菜单。
         高频:既読/未読(动态)+ アーカイブ + その他
         低频(进 「…」):削除 + 迷惑メール(其他后端未接,不展示)
      */}
      {selectedIds && selectedIds.size > 0 && onBatchAction && (
        <BulkToolbar
          selectedCount={selectedIds.size}
          hasAnyUnread={!!hasAnyUnreadSelected}
          pending={!!batchPending}
          isInSpamFolder={selectedFolderId === "spam" || selectedFolderId === "junk"}
          isInTrashFolder={selectedFolderId === "trash"}
          onMarkRead={() => onBatchAction("mark_read")}
          onMarkUnread={() => onBatchAction("mark_unread")}
          onArchive={() => onBatchAction("archive")}
          onTrash={() => onBatchAction("trash")}
          onSpam={() => onBatchAction("spam")}
          onRestoreToInbox={() => onBatchAction("restore_to_inbox")}
          onPermanentDelete={() => onBatchAction("permanent_delete")}
        />
      )}
      <div className="mv2-list-items">
        {messages.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: "#6b7280", fontSize: 13 }}>
            メールがありません
          </div>
        )}
        {messages.map((m) => {
          const isSelected = m.id === selectedMessageId;
          const isChecked = !!selectedIds?.has(m.id);
          return (
            <div
              key={m.id}
              className={`mv2-list-item${isSelected ? " selected" : ""}${!m.isRead ? " unread" : ""}`}
              onClick={() => onSelectMessage(m.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectMessage(m.id);
                }
              }}
            >
              <input
                type="checkbox"
                className="mv2-list-item-checkbox"
                checked={isChecked}
                disabled={!onToggleSelect}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelect?.(m.id);
                }}
                onChange={() => {}}
                aria-label={`${m.subject} を選択`}
              />
              <span className="mv2-list-item-dot" />
              <div className="mv2-list-item-content">
                <div className="mv2-list-item-row1">
                  {/* Draft (outbound 未发) 显式 显示 to_addresses 作为收件人。
                      Inbox (inbound) 显示 senderName / senderEmail。 */}
                  {m.folderId === "drafts" ? (
                    <span className="mv2-list-item-sender" title={m.senderEmail}>
                      宛先: {m.senderEmail || "(宛先なし)"}
                    </span>
                  ) : (
                    <span className="mv2-list-item-sender" title={m.senderEmail}>
                      {m.senderName || m.senderEmail}
                    </span>
                  )}
                  <span className="mv2-list-item-time">{formatRelativeTime(m.receivedAt)}</span>
                </div>
                <div className="mv2-list-item-row2">
                  <span className="mv2-list-item-subject">{m.subject}</span>
                </div>
                <div className="mv2-list-item-preview">{m.preview}</div>
              </div>
              <button
                type="button"
                className="mv2-toolbar-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleStar(m.id);
                }}
                aria-label={m.isStarred ? "スター解除" : "スター"}
                title={m.isStarred ? "スター解除" : "スター"}
              >
                <Star
                  size={18}
                  fill={m.isStarred ? "#f59e0b" : "none"}
                  color={m.isStarred ? "#f59e0b" : "#9ca3af"}
                />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// BulkToolbar: 批量操作栏(Phase 1.2 2026-07-23 重写)
//
// 高频区(图标按钮):既読/未読(动态)+ アーカイブ + その他「…」
// 低频区(popover):削除(危险) + 迷惑メール
//   ラベル / スヌーズ / 対応ステータス 后端未接,不展示占位项。
//
// 视觉:
//   - 单行 40px 高
//   - 左侧:件数
//   - 右侧:图标按钮 32×32 + 「…」触发菜单
//   - 不允许日文逐字换行;菜单用绝对定位浮在 toolbar 下方
// ============================================================
interface BulkToolbarProps {
  selectedCount: number;
  hasAnyUnread: boolean;
  pending: boolean;
  isInSpamFolder?: boolean;
  /** 2026-07-23:Trash folder 时 popover 显示「完全削除」替代「削除」 */
  isInTrashFolder?: boolean;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onArchive: () => void;
  onTrash: () => void;
  onSpam: () => void;
  onRestoreToInbox: () => void;
  onPermanentDelete: () => void;
}

function BulkToolbar({
  selectedCount,
  hasAnyUnread,
  pending,
  isInSpamFolder,
  isInTrashFolder,
  onMarkRead,
  onMarkUnread,
  onArchive,
  onTrash,
  onSpam,
  onRestoreToInbox,
  onPermanentDelete,
}: BulkToolbarProps) {
  const [moreOpen, setMoreOpen] = useState<boolean>(false);
  const moreRef = useRef<HTMLDivElement | null>(null);

  // 点外面关菜单
  useEffect(() => {
    if (!moreOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!moreRef.current) return;
      if (!moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [moreOpen]);

  const closeMore = useCallback(() => setMoreOpen(false), []);

  return (
    <div className="mv2-batch-toolbar" role="toolbar" aria-label="一括操作">
      <span className="mv2-batch-count" data-testid="mv2-batch-count">
        {selectedCount}件選択中
      </span>

      <div className="mv2-batch-actions">
        {/* 既読 / 未読 动态 */}
        {hasAnyUnread ? (
          <button
            type="button"
            className="mv2-batch-action-btn"
            onClick={onMarkRead}
            disabled={pending}
            aria-label="既読にする"
            title="既読にする"
            data-testid="mv2-batch-mark-read"
          >
            <MailOpen size={16} aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            className="mv2-batch-action-btn"
            onClick={onMarkUnread}
            disabled={pending}
            aria-label="未読にする"
            title="未読にする"
            data-testid="mv2-batch-mark-unread"
          >
            <MailOpen size={16} aria-hidden style={{ opacity: 0.5 }} />
          </button>
        )}

        {/* アーカイブ */}
        <button
          type="button"
          className="mv2-batch-action-btn"
          onClick={onArchive}
          disabled={pending}
          aria-label="アーカイブ"
          title="アーカイブ"
          data-testid="mv2-batch-archive"
        >
          <Archive size={16} aria-hidden />
        </button>

        {/* その他 popover */}
        <div className="mv2-batch-more" ref={moreRef}>
          <button
            type="button"
            className="mv2-batch-action-btn"
            onClick={() => setMoreOpen((v) => !v)}
            disabled={pending}
            aria-label="その他"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            title="その他"
            data-testid="mv2-batch-more"
          >
            <MoreHorizontal size={16} aria-hidden />
          </button>
          {moreOpen && (
            <div className="mv2-batch-more-menu" role="menu">
              {isInSpamFolder && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="mv2-batch-more-menu-item"
                    onClick={() => {
                      onRestoreToInbox();
                      closeMore();
                    }}
                    disabled={pending}
                  >
                    <Archive size={14} aria-hidden />
                    <span>受信トレイ</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="mv2-batch-more-menu-item danger"
                    onClick={() => {
                      onTrash();
                      closeMore();
                    }}
                    disabled={pending}
                  >
                    <Trash2 size={14} aria-hidden />
                    <span>削除</span>
                  </button>
                </>
              )}
              {isInTrashFolder && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="mv2-batch-more-menu-item"
                    onClick={() => {
                      onRestoreToInbox();
                      closeMore();
                    }}
                    disabled={pending}
                  >
                    <Archive size={14} aria-hidden />
                    <span>受信トレイ</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="mv2-batch-more-menu-item danger"
                    onClick={() => {
                      onPermanentDelete();
                      closeMore();
                    }}
                    disabled={pending}
                  >
                    <Trash2 size={14} aria-hidden />
                    <span>完全削除</span>
                  </button>
                </>
              )}
              {!isInSpamFolder && !isInTrashFolder && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="mv2-batch-more-menu-item"
                    onClick={() => {
                      onSpam();
                      closeMore();
                    }}
                    disabled={pending}
                  >
                    <AlertOctagon size={14} aria-hidden />
                    <span>迷惑メール</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="mv2-batch-more-menu-item danger"
                    onClick={() => {
                      onTrash();
                      closeMore();
                    }}
                    disabled={pending}
                  >
                    <Trash2 size={14} aria-hidden />
                    <span>削除</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
